import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  ZapIcon,
  ShieldIcon,
  GlobeIcon,
  CodeIcon,
  BarChart3Icon,
  LockIcon,
  CpuIcon,
  RefreshCwIcon,
  Sparkles,
  ArrowUpRight,
  CheckCircle2
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
  benefits: string[];
}

function FeatureCard({ icon, title, description, index, benefits }: FeatureCardProps) {
  const { isDark } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!cardRef.current) return;

    // Smooth entrance animation
    gsap.fromTo(
      cardRef.current,
      {
        opacity: 0,
        y: 40,
        scale: 0.95
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cardRef.current,
          start: 'top bottom-=80',
          toggleActions: 'play none none none',
          once: true
        },
        delay: index * 0.08
      }
    );
  }, [index]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || !glowRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    glowRef.current.style.background = `radial-gradient(circle at ${x}% ${y}%, rgba(6, 182, 212, 0.12), transparent 60%)`;
  };

  const handleMouseEnter = () => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        scale: 1.1,
        rotate: 8,
        duration: 0.4,
        ease: 'back.out(1.5)'
      });
    }
  };

  const handleMouseLeave = () => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        scale: 1,
        rotate: 0,
        duration: 0.4,
        ease: 'back.out(1.5)'
      });
    }
  };

  return (
    <motion.div
  ref={cardRef}
  onMouseMove={handleMouseMove}
  onMouseEnter={handleMouseEnter}
  onMouseLeave={handleMouseLeave}
  className="group relative h-full"
  whileHover={{ y: -20 }} // ab card 20px upar uthayega instead of 8px
  transition={{ type: 'spring', stiffness: 150, damping: 25 }} // slow aur smooth hover
>
      {/* Outer cyan glow */}
      <div className="absolute -inset-[2px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-cyan-500/40 via-cyan-400/30 to-transparent" 
           style={{ filter: 'blur(12px)' }} />

      {/* Card container */}
      <div
        className={`relative h-full min-h-[380px] lg:min-h-[380px] rounded-2xl p-8 border-2 transition-all duration-600 ${
          isDark
            ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 group-hover:border-cyan-500/60 shadow-xl'
            : 'bg-gradient-to-br from-white to-slate-50/80 border-slate-200/60 group-hover:border-cyan-400/80 shadow-xl'
        }`}
        style={{ backdropFilter: 'blur(8px)' }}
      >
        {/* Dynamic radial glow */}
        <div
          ref={glowRef}
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        />

        {/* Top gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Icon */}
        <div
          ref={iconRef}
          className={`relative inline-flex p-4 rounded-xl mb-6 transition-all duration-500 ${
            isDark
              ? 'bg-cyan-500/10 group-hover:bg-cyan-500/20 shadow-lg shadow-cyan-500/20'
              : 'bg-gradient-to-br from-cyan-100 to-blue-100 group-hover:from-cyan-200 group-hover:to-blue-200 shadow-lg shadow-cyan-500/10'
          }`}
        >
          <div className="relative z-10">
            {icon}
          </div>
          {/* Icon glow */}
          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-cyan-500/20" 
               style={{ filter: 'blur(8px)' }} />
        </div>

        {/* Number badge */}
        <div className={`absolute top-6 right-6 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ${
          isDark
            ? 'bg-slate-800/80 text-slate-500 group-hover:bg-cyan-500/20 group-hover:text-cyan-400'
            : 'bg-slate-100 text-slate-500 group-hover:bg-cyan-100 group-hover:text-cyan-600'
        }`}>
          {(index + 1).toString().padStart(2, '0')}
        </div>

        {/* Title */}
        <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${
          isDark ? 'text-white group-hover:text-cyan-400' : 'text-slate-900 group-hover:text-cyan-600'
        }`}>
          {title}
        </h3>

        {/* Description */}
        <p className={`leading-relaxed text-base mb-6 ${
          isDark ? 'text-slate-400' : 'text-slate-600'
        }`}>
          {description}
        </p>

        {/* Benefits - expandable */}
        <div className={`space-y-2 overflow-hidden transition-all duration-500 ${
          isExpanded ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'
        }`}>
          {benefits.map((benefit, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={isExpanded ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
              transition={{ delay: i * 0.1 }}
              className="flex items-start gap-2"
            >
              <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                isDark ? 'text-cyan-400' : 'text-cyan-600'
              }`} />
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {benefit}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Learn more button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`group/btn inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ${
            isDark
              ? 'bg-slate-800/50 text-slate-400 hover:bg-cyan-500/10 hover:text-cyan-400 border border-slate-700/50 hover:border-cyan-500/30'
              : 'bg-slate-100 text-slate-600 hover:bg-cyan-100 hover:text-cyan-700 border border-slate-200 hover:border-cyan-400'
          }`}
        >
          {isExpanded ? 'Show Less' : 'Learn More'}
          <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
        </button>

        {/* Bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl overflow-hidden">
          <div className={`h-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out ${
            isDark
              ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500'
              : 'bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
          }`} />
        </div>
      </div>
    </motion.div>
  );
}

export function Features() {
  const { isDark } = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);

  const features = [
    {
      icon: <ZapIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Instant Settlements',
      description: 'Receive funds in real-time with our instant settlement engine. No more waiting days for your money.',
      benefits: [
        'Same-day payouts to your bank account',
        'Real-time balance updates',
        'Automatic reconciliation'
      ]
    },
    {
      icon: <ShieldIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Fraud Detection',
      description: 'AI-powered fraud prevention that adapts to new threats in real-time, protecting every transaction.',
      benefits: [
        'Machine learning threat detection',
        'Custom risk rules engine',
        'Chargeback prevention tools'
      ]
    },
    {
      icon: <GlobeIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Multi-Currency',
      description: 'Accept and settle in 135+ currencies with automatic conversion and competitive FX rates.',
      benefits: [
        'Support for 135+ currencies',
        'Competitive exchange rates',
        'Automatic currency conversion'
      ]
    },
    {
      icon: <CodeIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'API-First Design',
      description: 'RESTful APIs with comprehensive SDKs for every major language. Integrate in minutes.',
      benefits: [
        'SDKs for 10+ languages',
        'Comprehensive documentation',
        'Webhook notifications'
      ]
    },
    {
      icon: <BarChart3Icon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Analytics Dashboard',
      description: 'Real-time insights into payment flows. Track revenue, monitor disputes, optimize rates.',
      benefits: [
        'Real-time transaction monitoring',
        'Custom report builder',
        'Revenue forecasting'
      ]
    },
    {
      icon: <LockIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'PCI Compliance',
      description: 'Level 1 PCI DSS certified. We handle security complexity so you can focus on building.',
      benefits: [
        'Level 1 PCI DSS certified',
        'SOC 2 Type II compliant',
        'End-to-end encryption'
      ]
    },
    {
      icon: <CpuIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Smart Routing',
      description: 'Intelligent payment routing across multiple processors to maximize approval rates globally.',
      benefits: [
        'Multi-processor failover',
        'Optimize for lowest fees',
        'Geographic routing rules'
      ]
    },
    {
      icon: <RefreshCwIcon className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Auto Retry',
      description: 'Automatic retry logic with smart scheduling recovers failed payments without manual effort.',
      benefits: [
        'Smart retry algorithms',
        'Customizable retry schedules',
        'Success rate optimization'
      ]
    },
  ];

  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: headerRef.current,
        start: 'top 80%',
        toggleActions: 'play none none none',
        once: true
      }
    });

    tl.fromTo(
      badgeRef.current,
      { opacity: 0, y: -15, scale: 0.9 },
      { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'back.out(1.5)' }
    )
    .fromTo(
      titleRef.current,
      { opacity: 0, y: 25 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
      '-=0.3'
    )
    .fromTo(
      descRef.current,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' },
      '-=0.4'
    );
  }, []);

  return (
    <section
      ref={sectionRef}
      id="features"
      className="relative py-32 px-6 sm:px-8 lg:px-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-20">
          <span
            ref={badgeRef}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold tracking-wide uppercase mb-6 ${
              isDark
                ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-700 border border-cyan-300'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Features
          </span>

          <h2
            ref={titleRef}
            className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Everything you need to{' '}
            <span className={`relative inline-block ${
              isDark
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600'
            }`}>
              scale
            </span>
          </h2>

          <p
            ref={descRef}
            className={`text-lg md:text-xl max-w-3xl mx-auto leading-relaxed ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}
          >
            A complete payment toolkit designed for modern businesses. From instant settlements to advanced fraud detection.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
          {features.map((feat, idx) => (
            <FeatureCard
              key={idx}
              icon={feat.icon}
              title={feat.title}
              description={feat.description}
              benefits={feat.benefits}
              index={idx}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center mt-20"
        >
          <p className={`text-lg mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Want to see how it all works together?
          </p>
          <a
            href="#demo"
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-bold transition-all duration-300 hover:scale-105 active:scale-95 ${
              isDark
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-xl shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-500/40'
                : 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-xl shadow-cyan-600/30 hover:shadow-2xl hover:shadow-cyan-600/40'
            }`}
          >
            Schedule a Demo
            <Sparkles className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}